/**
 * Utility functions for media handling
 */

import { MediaItem, VideoVariant, MediaGridLayout } from './types';

/**
 * Get the best quality MP4 video URL from variants
 * Selects highest bitrate MP4, ignoring HLS streams
 */
export const getBestVideoUrl = (variants?: VideoVariant[]): string | null => {
  if (!variants || variants.length === 0) return null;

  // Filter to only MP4 variants (exclude HLS .m3u8)
  const mp4Variants = variants.filter((v) => v.content_type === 'video/mp4');
  if (mp4Variants.length === 0) return null;

  // Sort by bitrate (highest first) and return best quality
  const sorted = mp4Variants.sort((a, b) => (b.bit_rate || 0) - (a.bit_rate || 0));
  return sorted[0].url;
};

/**
 * Get HLS stream URL if available
 */
export const getHlsUrl = (variants?: VideoVariant[]): string | null => {
  if (!variants || variants.length === 0) return null;
  
  const hls = variants.find((v) => v.content_type === 'application/x-mpegURL');
  return hls?.url || null;
};

/**
 * Get all available MP4 quality options sorted by bitrate
 */
export const getVideoQualities = (variants?: VideoVariant[]): VideoVariant[] => {
  if (!variants || variants.length === 0) return [];
  
  return variants
    .filter((v) => v.content_type === 'video/mp4')
    .sort((a, b) => (b.bit_rate || 0) - (a.bit_rate || 0));
};

/**
 * Format video duration from milliseconds to human-readable string
 */
export const formatDuration = (ms?: number): string => {
  if (!ms) return '';
  
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  
  if (minutes === 0) {
    return `${seconds}s`;
  }
  
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Format duration as MM:SS for video player
 */
export const formatDurationLong = (ms?: number): string => {
  if (!ms) return '0:00';
  
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Check if media item is a video or animated GIF
 */
export const isVideoMedia = (media: MediaItem): boolean => {
  return media.type === 'video' || media.type === 'animated_gif';
};

/**
 * Check if video has playable variants
 */
export const hasPlayableVideo = (media: MediaItem): boolean => {
  return isVideoMedia(media) && !!getBestVideoUrl(media.variants);
};

/**
 * Get the display URL for a media item (thumbnail for video, URL for photo)
 */
export const getDisplayUrl = (media: MediaItem): string => {
  if (isVideoMedia(media)) {
    return media.preview_image_url || '';
  }
  return media.url || media.preview_image_url || '';
};

/**
 * Get the largest version of a Twitter/X image URL
 * Twitter URLs often have size params like ?format=jpg&name=small
 * We want to use 'large' for best quality
 */
export const getLargestImageUrl = (url: string): string => {
  if (!url) return url;
  
  // Check if it's a Twitter/X media URL
  if (url.includes('pbs.twimg.com') || url.includes('twimg.com')) {
    // Remove existing name parameter
    let baseUrl = url.replace(/([?&])name=[^&]+/gi, '$1').replace(/[?&]$/, '');
    
    // Add or update to use the largest size
    if (baseUrl.includes('?')) {
      if (!baseUrl.includes('name=')) {
        baseUrl = `${baseUrl}&name=large`;
      }
    } else {
      baseUrl = `${baseUrl}?name=large`;
    }
    return baseUrl;
  }
  
  return url;
};

/**
 * Extract image URLs from media array for lightbox
 * Returns the largest available version of each image
 */
export const getImageUrls = (media: MediaItem[]): string[] => {
  return media
    .filter((m) => !isVideoMedia(m))
    .map((m) => getLargestImageUrl(m.url || m.preview_image_url || ''))
    .filter(Boolean);
};

/**
 * Determine grid layout based on media count
 */
export const getGridLayout = (count: number): MediaGridLayout => {
  if (count >= 4) return 4;
  if (count === 3) return 3;
  if (count === 2) return 2;
  return 1;
};

/**
 * Convert legacy media_urls to MediaItem array
 */
export const convertLegacyMedia = (mediaUrls: string[]): MediaItem[] => {
  return mediaUrls.map((url) => ({
    type: 'photo' as const,
    url,
    preview_image_url: url,
  }));
};

/**
 * Get bitrate label for quality selector
 */
export const getBitrateLabel = (bitRate?: number): string => {
  if (!bitRate) return 'Unknown';
  
  const kbps = bitRate / 1000;
  if (kbps >= 2000) return 'HD (1080p)';
  if (kbps >= 1000) return 'HD (720p)';
  if (kbps >= 500) return 'SD (480p)';
  return 'Low (360p)';
};

